import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { ToastItem, ToastIntent } from 'tio-reimagined/components/toast';

// ---------------------------------------------------------------------------
// ReimaginedToast – service for managing toast notifications
// ---------------------------------------------------------------------------

interface ToastOptions {
  /** Duration in ms before auto-dismiss. 0 = persistent. */
  duration?: number;
  /** Optional action label */
  actionLabel?: string;
  /** Callback when action is clicked */
  onAction?: () => void;
}

/** Default durations per intent */
const DEFAULT_DURATIONS: Record<ToastIntent, number> = {
  success: 5000,
  info: 5000,
  warning: 8000,
  error: 0, // persistent — user must dismiss
};

let nextId = 0;

/**
 * Reimagined Toast Service — manages the stack of toast notifications.
 *
 * Inject as `@service('reimagined-toast') declare toast: ReimaginedToastService;`
 * and call `this.toast.success('Saved!')`, `this.toast.error('Failed')`, etc.
 *
 * Pair with `<ToastContainer @toasts={{this.toast.toasts}} @onDismiss={{this.toast.dismiss}} />`
 * in your layout template.
 */
export default class ReimaginedToastService extends Service {
  @tracked private _toasts: ToastItem[] = [];

  get toasts(): ToastItem[] {
    return this._toasts;
  }

  // Public convenience methods

  success = (message: string, options?: ToastOptions) => {
    this.add(message, 'success', options);
  };

  error = (message: string, options?: ToastOptions) => {
    this.add(message, 'error', options);
  };

  warning = (message: string, options?: ToastOptions) => {
    this.add(message, 'warning', options);
  };

  info = (message: string, options?: ToastOptions) => {
    this.add(message, 'info', options);
  };

  /**
   * Generic add method — matches Frontile's `notifications.add()` for easier migration.
   */
  add = (message: string, intent: ToastIntent = 'info', options?: ToastOptions) => {
    const id = `toast-${++nextId}`;
    const duration = options?.duration ?? DEFAULT_DURATIONS[intent] ?? 5000;

    const toast: ToastItem = {
      id,
      message,
      intent,
      duration,
      visible: false,
      actionLabel: options?.actionLabel,
      onAction: options?.onAction,
    };

    // Add toast to array
    this._toasts = [...this._toasts, toast];

    // Make visible on next frame (triggers the slide-in animation)
    requestAnimationFrame(() => {
      this._toasts = this._toasts.map((t) => (t.id === id ? { ...t, visible: true } : t));
    });

    // Auto-dismiss after duration (if duration > 0)
    if (duration > 0) {
      setTimeout(() => {
        this.dismiss(id);
      }, duration);
    }
  };

  /**
   * Dismiss a toast by ID (triggers exit animation, then removes).
   */
  dismiss = (id: string) => {
    // Trigger exit animation
    this._toasts = this._toasts.map((t) => (t.id === id ? { ...t, visible: false } : t));

    // Remove from DOM after animation
    setTimeout(() => {
      this._toasts = this._toasts.filter((t) => t.id !== id);
    }, 300);
  };

  /** Clear all toasts */
  clear = () => {
    // Trigger exit on all
    this._toasts = this._toasts.map((t) => ({ ...t, visible: false }));
    setTimeout(() => {
      this._toasts = [];
    }, 300);
  };
}
